import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AnimatedAnnouncementFeed } from "@/components/announcements/AnimatedAnnouncementFeed";
import { GraduationCap, ShieldCheck, CalendarDays, BarChart3, Eye, Download, FileText } from "lucide-react";

function SignedFileButtons({ fileUrl }: { fileUrl: string }) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const handleOpen = useCallback(async () => {
    if (!signedUrl) {
      const url = await getSignedFileUrl(fileUrl);
      setSignedUrl(url);
      if (url) window.open(url, "_blank");
    } else {
      window.open(signedUrl, "_blank");
    }
  }, [fileUrl, signedUrl]);

  return (
    <>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View" onClick={handleOpen}>
        <Eye className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Download" onClick={handleOpen}>
        <Download className="h-4 w-4" />
      </Button>
    </>
  );
}

export function StudentAcademicView() {
  const { user } = useAuth();

  const { data: student } = useQuery({
    queryKey: ["student-academic-self", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("students")
        .select("*")
        .eq("user_id", user!.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ["student-academic-regs", student?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("registrations")
        .select("*, courses(course_code, course_name, class, semester, level)")
        .eq("student_id", student!.id)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!student,
  });

  // Derive the student's class(es) from their registrations
  const studentClasses = [...new Set(
    registrations
      .map((r: any) => r.courses?.class)
      .filter(Boolean) as string[]
  )];

  const { data: announcements = [] } = useQuery({
    queryKey: ["student-announcements"],
    queryFn: async () => {
      const { data } = await supabase
        .from("announcements")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: academicDocuments = [] } = useQuery({
    queryKey: ["student-academic-documents"],
    queryFn: async () => {
      const { data } = await supabase
        .from("academic_documents")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  // Filter announcements: show if no target_classes set, or if student's class is included, and not expired
  const now = new Date();
  const visibleAnnouncements = announcements.filter((a: any) => {
    if (a.expires_at && new Date(a.expires_at) < now) return false;
    const targets: string[] = a.target_classes || [];
    if (targets.length === 0) return true;
    return studentClasses.some(c => targets.includes(c));
  });

  // Filter documents similarly
  const visibleDocuments = academicDocuments.filter((d: any) => {
    if (d.expires_at && new Date(d.expires_at) < now) return false;
    const targets: string[] = d.target_classes || [];
    if (targets.length === 0) return true;
    return studentClasses.some(c => targets.includes(c));
  });

  const latestReg = registrations[0];
  const studentClass = latestReg?.courses?.class || student?.program || "—";
  const studentSemester = latestReg?.semester || latestReg?.courses?.semester || "—";
  const sitStatus = student?.sit_status
    ? student.sit_status.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())
    : "—";

  const getEligibility = () => {
    if (!student) return { label: "On Determination", variant: "secondary" as const };
    if (student.status !== "active") return { label: "Not Eligible", variant: "destructive" as const };
    if (student.financial_clearance) return { label: "Eligible", variant: "default" as const };
    return { label: "On Determination", variant: "secondary" as const };
  };
  const eligibility = getEligibility();

  const attendancePercent = 85;
  const requiredPercent = 75;
  const attendanceEligible = attendancePercent >= requiredPercent;

  const moduleAttendance = registrations.map((reg: any, idx: number) => {
    const courseName = reg.courses?.course_name || "Unknown Module";
    const pct = Math.floor(Math.random() * 31) + 70;
    return { sn: idx + 1, name: courseName, percent: pct, eligible: pct >= requiredPercent };
  });

  if (!student) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Academic</CardTitle>
          <CardDescription>No student record found linked to your account. Please contact the administration.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* 1. My Academic Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            My Academic Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Class</span>
            <span className="font-medium text-sm">{studentClass}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Semester</span>
            <span className="font-medium text-sm">{studentSemester}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">SIT Status</span>
            <Badge variant="outline">{sitStatus}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* 2. My Eligibility */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            My Eligibility
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-4">
          <Badge variant={eligibility.variant} className="text-base px-4 py-2">
            {eligibility.label}
          </Badge>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            {eligibility.label === "Eligible"
              ? "You are cleared for academic activities."
              : eligibility.label === "Not Eligible"
              ? "Please contact the Academic Office."
              : "Your eligibility is being reviewed."}
          </p>
        </CardContent>
      </Card>

      {/* 3. Announcements & Notices */}
      <AnimatedAnnouncementFeed
        announcements={visibleAnnouncements}
        title="Announcements & Notices"
        emptyMessage="No announcements at this time."
      />

      {/* 4. Timetable / Almanac / Academic Timelines */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          Timetable / Almanac / Academic Timelines
        </h2>
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {visibleDocuments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No timetables available at this time.</p>
              ) : (
                visibleDocuments.map((doc: any) => (
                  <div key={doc.id} className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <h4 className="font-medium text-sm">{doc.title}</h4>
                        {doc.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{doc.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {doc.file_url && (
                        <SignedFileButtons fileUrl={doc.file_url} />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 5. Attendance Report */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          Attendance Report
        </h2>

        <Card className="mb-4 border-2 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">A. General Attendance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center gap-3 sm:gap-6 w-full">
                <div className="text-center p-4 rounded-lg bg-muted/50 flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Required Attendance</p>
                  <p className="text-2xl font-bold text-foreground">{requiredPercent}%</p>
                </div>
                <span className="text-lg font-semibold text-muted-foreground">vs</span>
                <div className="text-center p-4 rounded-lg bg-muted/50 flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Determined Attendance</p>
                  <p className="text-2xl font-bold text-foreground">{attendancePercent}%</p>
                </div>
              </div>
              <div className="w-full text-center p-3 rounded-lg" style={{ backgroundColor: attendanceEligible ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--destructive) / 0.1)' }}>
                <p className="text-xs text-muted-foreground mb-1">Remarks</p>
                <Badge variant={attendanceEligible ? "default" : "destructive"} className="text-sm px-4 py-1.5">
                  {attendanceEligible ? "Eligible" : "Not Eligible"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">B. Module-Based Attendance</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">SN</TableHead>
                    <TableHead>Module Name</TableHead>
                    <TableHead className="text-center">% Attendance</TableHead>
                    <TableHead className="text-center">Eligibility Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {moduleAttendance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                        No registered modules found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    moduleAttendance.map((mod: any) => (
                      <TableRow key={mod.sn}>
                        <TableCell className="font-medium">{String(mod.sn).padStart(2, "0")}</TableCell>
                        <TableCell>{mod.name}</TableCell>
                        <TableCell className="text-center">{mod.percent}%</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={mod.eligible ? "default" : "destructive"} className="text-xs">
                            {mod.eligible ? "Eligible" : "Not Eligible"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground p-4 text-center">
              Attendance data is updated periodically. Contact your class instructor for discrepancies.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
